import SubmitButton from "components/Buttons/SubmitButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { addUnit, fetchUnitById, updateUnit } from "slices/unit/thunk";
import * as Yup from "yup";

const UnitForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const dispatch = useDispatch<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [initialData, setInitialData] = useState({
    unit_name: "",
    unit_symbol: "",
  });

  useEffect(() => {
    if (isEdit && id) {
      setIsLoading(true);
      dispatch(fetchUnitById(id))
        .unwrap()
        .then((res: any) => {
          setInitialData({
            unit_name: res.unit_name,
            unit_symbol: res.unit_symbol,
          });
        })
        .catch((error: any) => {
          toast.error("Failed to load unit details");
          console.error("Fetch unit error:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [dispatch, id, isEdit]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      unit_name: initialData.unit_name || "",
      unit_symbol: initialData.unit_symbol || "",
    },
    validationSchema: Yup.object({
      unit_name: Yup.string()
        .required("Please enter unit name"),
      unit_symbol: Yup.string().required("Please enter unit symbol"),
    }),
    onSubmit: async (values) => {
      const payload = {
        unit_name: values.unit_name,
        unit_symbol: values.unit_symbol,
      };

      try {
        if (isEdit) {
          await dispatch(updateUnit({ id, ...payload })).unwrap();
          toast.success("Unit updated successfully");
        } else {
          await dispatch(addUnit(payload)).unwrap();
          toast.success("Unit added successfully");
        }
        navigate("/unit", { state: { clearState: !isEdit } });
      } catch (error: any) {
        // console.error("Unit operation failed:", error);
        toast.error(error?.message || "Something went wrong");
      }
    },
  });

  return (
    <div className="page-content px-4">
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          padding: "30px",
          boxShadow: "0 0 15px rgba(0,0,0,0.05)",
        }}
      >
        <FormHeader
          title={isEdit ? "Edit Unit" : "Add Unit"}
          firstLabel="Unit"
          secondLabel={isEdit ? "Edit Unit" : "Add Unit"}
          handleArrowClick={() => navigate("/unit")}
        />
        {isEdit && isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Form autoComplete="off" onSubmit={formik.handleSubmit}>
            <Row>
              <Col lg={6}>
                <div className="mb-3">
                  <Form.Label>
                    Unit Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="unit_name"
                    placeholder="Enter Unit Name"
                    value={formik.values.unit_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.unit_name && formik.touched.unit_name
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.unit_name}
                  </Form.Control.Feedback>
                </div>
              </Col>

              <Col lg={6}>
                <div className="mb-3">
                  <Form.Label>
                    Unit Symbol <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="unit_symbol"
                    placeholder="Enter Unit Symbol"
                    value={formik.values.unit_symbol}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.unit_symbol && formik.touched.unit_symbol
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.unit_symbol}
                  </Form.Control.Feedback>
                </div>
              </Col>
            </Row>


            <div className="d-flex justify-content-center gap-3 py-3">
              <CancelBackButton to="/unit" />

              {/* Reusable submit button with spinner */}
              <SubmitButton
                isSubmitting={formik.isSubmitting}
                isEdit={isEdit}
              />
            </div>
          </Form>
        )}
      </div>
    </div>
  );
};

export default UnitForm;
